import type { Theme } from '@mui/material/styles'
import tinycolor from 'tinycolor2'
import { v4 as uuid } from 'uuid'
import { BLANK_SERIES_ID, ChartViewKey, INITIAL_CANDLE_COUNT, VOLUME_INDICATOR_CODE } from '../assets/constants'
import { ChartSubType, ChartType } from '../proto/generated/chart'
import {
    BarsInfo,
    ChartOptions,
    DeepPartial,
    ITimeScaleApi,
    LineStyle,
    LineType,
    LineWidth,
    LogicalRange,
    Point,
    PriceFormat,
    PriceScaleMode,
    Time,
    UTCTimestamp
} from 'lightweight-charts'
import {
    ChartCandle,
    ChartConfig,
    ComparableSecurityPanePlacement,
    ContractSwitch,
    CreateIndicatorDataParams,
    IndicatorDataLoadCallback,
    IndicatorDrawCallback,
    IndicatorFillCallback,
    IndicatorLineCallback,
    IndicatorPlotCallback,
    PaneConfig,
    Scale,
    Series,
    SeriesData,
    SeriesGroup
} from '../components/chartV2/chart.typings'
import {
    GetCurrentOpenInterestResponse_ExpiryOpenInterest,
    GetCurrentOpenInterestResponse_ExpiryOpenInterest_StrikeOpenInterest,
    GetSeriesResponse,
    GetSeriesResponse_DataPoint,
    GetSeriesResponse_Series
} from '../proto/generated/candle-data-depot'
import { Indicator_Format, IndicatorColor, IndicatorOutput_OutputStyle, IndicatorOutput_OutputType } from '../proto/generated/indicator'
import { IndicatorUtils } from './IndicatorUtils/indicatorUtils'
import { CandleTypes } from '../components/chartV2/chart.enums'
import { DrawOptions, FillOptions, HLineOptions, LineOptions, PlotOptions } from '../pages/chartPage/ChartPage'
import { compareDates, convertToTimeZone, getTimeInSeconds, isWeekend, TimeZone, toDayStart, parseYYYYMMDDDate, convertToUTC, utcToIST } from './dateTime'
import {
    SuperChartConfiguration_DateRange,
    SuperChartConfiguration_TimeBucket
} from '../proto/generated/superchart-page'
import { TimeBucket, TimeBucket_TimeSeries } from '../proto/generated/common'
import { formatDateToISODate, replaceColorHashcodes, toLocaleStringIn } from './general'
import { getTimeIndex } from './getTimeIndex'
import { formatLargeNumber } from './screener'
import { Security } from '../proto/generated/security'
import { getSecurityAsync, getSecurityKey } from './security'
import { getCandles, getSeriesData } from '../services/apis/candles'
import { DrawingSettingsMap } from '../components/chartV2/drawingTools/drawing.interface'
import type { OiProfileDataType } from '../components/chartV2/drawingTools/oiProfile/oiProfile'
import {
    clampOiProfileBarThickness,
    clampOiProfileBarWidthPercent,
    OI_PROFILE_DEFAULT_BAR_THICKNESS,
    OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT,
    OI_PROFILE_DEFAULT_PRIMARY_CALL_COLOR,
    OI_PROFILE_DEFAULT_PRIMARY_PUT_COLOR,
    OI_PROFILE_DEFAULT_SECONDARY_CALL_COLOR,
    OI_PROFILE_DEFAULT_SECONDARY_PUT_COLOR,
    resolveOiProfileColor,
} from '../components/chartV2/drawingTools/oiProfile/oiProfile.constants'
import {
    chartLineStyleToLineType,
    CP_DEFAULT_BACKGROUND_COLOR,
    CP_DEFAULT_NECK_LINE_COLOR,
    CP_DEFAULT_PANE_LABEL_COLOR,
    CP_DEFAULT_PANE_LABEL_TEXT_COLOR,
    CP_DEFAULT_PRICE_LINE_COLOR,
    getPatternDrawingSettingsMap,
    resolveChartPatternOverlaySettings,
} from '../components/chartV2/drawingTools/chartPatternOverlayTool/chartPatternOverlay.constants'
import { getTimeWidthInSeconds } from '../components/chartV2/chartHelpers'
import { candleUpColorAlpha } from '../assets/colors'
import { Candle } from '../proto/generated/candle-data-depot-common'
import { SpecialTradingDay, TradingCalendarData, TradingSession } from '../proto/generated/trading-calendar'
import { getNextValidTradingDay } from './tradingCalendar'
import { calculateCandlesBetweenDates } from './chartUtils'
import { SeriesGroupCategory } from '../pages/home'
import { convertExpressionToDisplayExpression } from './expression'
import { sanitizeId, decodeIndicatorOutputSource, resolveIndicatorInputValue } from './indicators'
import { getSecondsValue } from '../components/timeBuckets/TimeBuckets'
import { isIntraday } from './timeBuckets'
import { convertMapToKeyValueArray } from './common'
import { defaultCanvasSettings } from './canvasSettingsDefaults'

export type TradingDaySession = TradingSession & {
    date: Date
}

export type CalendarData = {
    holidays: Set<string>,
    specialTradingDays: Set<string>,
    dayToSessions: Map<string, string[]>,
    typeToSessions: Map<string, TradingSession>
}

export enum DataUpdateRequestType {
    INITIAL,
    HISTORICAL,
    LIVE
}

interface InputSeriesParams {
    title: string,
    defval: string,
    code?: string
}

export type ViewportCandleInfo = {
    barsBefore: number,
    barsAfter: number,
    rangeStart: number,
    rangeEnd: number,
    startTime: number,
    middlePointTime?: number,
    endTime: number,
    viewportCandles: number
}

export type CrossHairInfo = {
    point: Point,
    time: number,
    price: number
}

export const areTwoRangesAlmostSame = (range1: ViewportCandleInfo, range2: ViewportCandleInfo, timeScaleApi: ITimeScaleApi<Time>) => {
    const candlesDiff = Math.abs(range1.viewportCandles - range2.viewportCandles)
    if (candlesDiff > 0.1 * range1.viewportCandles) return false

    const width = timeScaleApi.width()

    if (Number.isFinite(range1.middlePointTime) && Number.isFinite(range2.middlePointTime)) {
        const x1 = timeScaleApi.timeToCoordinate(range1.middlePointTime as Time)
        const x2 = timeScaleApi.timeToCoordinate(range2.middlePointTime as Time)
        if (x1 && x2) {
            if (Math.abs(x1 - x2) > 0.1 * width) return false
        }
    }
    if (Number.isFinite(range1.startTime) && Number.isFinite(range2.startTime)) {
        const x1 = timeScaleApi.timeToCoordinate(range1.startTime as Time)
        const x2 = timeScaleApi.timeToCoordinate(range2.startTime as Time)
        if (x1 && x2) {
            if (Math.abs(x1 - x2) > 0.1 * width) return false
        }
    }
    if (Number.isFinite(range1.endTime) && Number.isFinite(range2.endTime)) {
        const x1 = timeScaleApi.timeToCoordinate(range1.endTime as Time)
        const x2 = timeScaleApi.timeToCoordinate(range2.endTime as Time)
        if (x1 && x2) {
            if (Math.abs(x1 - x2) > 0.1 * width) return false
        }
    }

    return true

}

export function getLineWidth(thickness: number): LineWidth {
    if (Math.round(thickness) < 1) return 1
    if (Math.round(thickness) > 4) return 4
    return Math.round(thickness) as LineWidth
}

export function constructCalendarDataLookup(calendarData: TradingCalendarData | undefined): CalendarData {
    const typeToSessions: Map<string, TradingSession> = new Map()
    calendarData?.tradingSessions?.forEach((session: TradingSession) => {
        typeToSessions.set(session.type, session)
    })

    const dayToSessions: Map<string, string[]> = new Map()
    calendarData?.specialTradingDays?.forEach((std: SpecialTradingDay) => {
        dayToSessions.set(std.date, std.sessionTypes)
    })

    return {
        holidays: new Set(calendarData?.holidays?.map((holiday) => holiday.date)),
        specialTradingDays: new Set(calendarData?.specialTradingDays?.map((std) => std.date)),
        dayToSessions,
        typeToSessions
    } as CalendarData
}

export function getCandlesInBetween({ candleOne, candleTwo, timeBucket, calendarData, timezone }: { candleOne: ChartCandle, candleTwo: ChartCandle, timeBucket: SuperChartConfiguration_TimeBucket, calendarData: TradingCalendarData | undefined, timezone: TimeZone }): ChartCandle[] {
    // if(!candleOne || !candleTwo) return []
    let time1InSeconds = convertToUTC(candleOne.time as number, timezone)
    const time2InSeconds = convertToUTC(candleTwo.time as number, timezone)

    const daySessions: TradingDaySession[] = []
    let d1 = new Date(time1InSeconds * 1000)
    const initialD1 = new Date(d1);
    let d2 = new Date(time2InSeconds * 1000)
    const calendarDataLookup = constructCalendarDataLookup(calendarData)
    if (!areOfDifferentDays(time1InSeconds * 1000, time2InSeconds * 1000)) {
        daySessions.push(...onOrBetweenTradingSessionsOfTheDay(time1InSeconds * 1000, time2InSeconds * 1000, calendarDataLookup).map((session) => {
            return {
                ...session,
                date: new Date(d1)
            }
        }))
    } else {
        while (compareDates(d1, d2) <= 0) {
            if (isTradingHoliday(formatDateToISODate(d1, ''), calendarDataLookup)) {
                d1.setDate(d1.getDate() + 1)
                continue;
            }
            if (compareDates(d1, d2) == 0) {
                daySessions.push(...onOrBetweenTradingSessionsOfTheDay(d1.getTime(), d2.getTime(), calendarDataLookup).map((session) => {
                    return {
                        ...session,
                        date: new Date(d1)
                    }
                }))
            } else if (compareDates(d1, initialD1) == 0) {
                daySessions.push(...onOrNextTradingSessionsOfTheDay(d1.getTime(), calendarDataLookup).map((session) => {
                    return {
                        ...session,
                        date: new Date(d1)
                    }
                }))
            } else {
                daySessions.push(...getTradingSessionsOfTheDay(d1, calendarDataLookup).map((session) => {
                    return {
                        ...session,
                        date: new Date(d1)
                    }
                }))
            }
            d1.setDate(d1.getDate() + 1)
        }
    }



    const candles: ChartCandle[] = []

    const timeWidth = getSecondsValue(timeBucket)
    while (time2InSeconds - time1InSeconds > timeWidth) {
        if (!daySessions.length) break
        time1InSeconds += timeWidth

        if (time1InSeconds >= convertToUTC(getEndTimeInSecondsFromTradingDaySession(daySessions[0]), timezone)) {
            daySessions.shift()
            if (daySessions.length) {
                time1InSeconds = convertToUTC(getStartTimeInSecondsFromTradingDaySession(daySessions[0]), timezone)
            }
        }
        if (time1InSeconds < time2InSeconds) {
            candles.push({ ...candleOne, volume: 0, time: convertToTimeZone(time1InSeconds, timezone) as Time })
        }
    }

    return candles
}

function areOfDifferentDays(timestampOne: number, timestampTwo: number): boolean {
    const dt1 = new Date(timestampOne)
    const dt2 = new Date(timestampTwo)

    if (`${dt1.getFullYear()}-${dt1.getMonth()}-${dt1.getDate()}` !== `${dt2.getFullYear()}-${dt2.getMonth()}-${dt2.getDate()}`) return true
    return false;
}

export const isSpecialChartType = (chartType: ChartType): boolean => {
    //can be compared with any number of special chart types, when added in future
    return [ChartType.KAGI, ChartType.LINE_BREAK, ChartType.POINT_AND_FIGURE, ChartType.RANGE, ChartType.RENKO].includes(chartType)
}

/** Renko / Kagi / P&F transform OHLC into price-based bars with fewer points than the source series. */
export const isTransformedChartType = (chartType: ChartType): boolean => {
    return [ChartType.KAGI, ChartType.POINT_AND_FIGURE, ChartType.RENKO].includes(chartType)
}

export const getNewPanesConfig = (panes: string[], panesConfig: Record<string, PaneConfig>, seriesGroups: Record<string, SeriesGroup>, series: Record<string, Series>, reset = false) => {
    const newPanes: string[] = []
    const newPanesConfig: Record<string, PaneConfig> = {}
    const newSeriesGroups: Record<string, SeriesGroup> = {}
    const newSeries: Record<string, Series> = {}

    panes.forEach((id) => {
        const paneConfig = { ...panesConfig[id] }
        if (reset) paneConfig.id = uuid()
        newPanes.push(paneConfig.id)
        const newSrsGrpList: string[] = []
        paneConfig.seriesGroups.forEach((sgid) => {
            const srsGrp = { ...seriesGroups[sgid] }
            if (reset) srsGrp.id = uuid()
            newSrsGrpList.push(srsGrp.id)
            const newSrsList: string[] = []
            srsGrp.series.forEach((sid) => {
                const srs = { ...series[sid] }
                if (reset) srs.id = uuid()
                newSrsList.push(srs.id)
                newSeries[srs.id] = { ...srs }
            })
            srsGrp.series = newSrsList
            newSeriesGroups[srsGrp.id] = srsGrp
        })
        paneConfig.seriesGroups = newSrsGrpList
        newPanesConfig[paneConfig.id] = paneConfig
    })
    return {
        panes: newPanes,
        panesConfig: newPanesConfig,
        seriesGroups: newSeriesGroups,
        series: newSeries
    }
}

export const getViewportCandleInfo = (range: LogicalRange, barsInfo: BarsInfo<any>, data: SeriesData[]): ViewportCandleInfo => {
    const rangeStart = Math.floor(range.from)
    const rangeEnd = Math.ceil(range.to)
    const barsBefore = Math.floor(barsInfo.barsBefore)
    const barsAfter = (barsInfo.barsAfter < 0 ? Math.floor(barsInfo.barsAfter) : Math.ceil(barsInfo.barsAfter))

    const startTime = rangeStart > 0 && data[rangeStart] ? data[rangeStart].time as number : data[0]?.time as number
    const endTime = rangeEnd > 0 && data[rangeEnd] ? data[rangeEnd]?.time as number : data[data.length - 1]?.time as number

    return {
        barsBefore,
        barsAfter,
        rangeStart,
        rangeEnd,
        startTime,
        middlePointTime: getMiddlePointTime(range, data),
        endTime,
        viewportCandles: rangeEnd - rangeStart
    }
}

export const getMiddlePointTime = (range: LogicalRange, data: SeriesData[]): number => {
    const rangeStart = Math.floor(range.from)
    const rangeEnd = range.to < 0 ? 0 : Math.ceil(range.to)
    let midPoint = Math.floor(rangeStart + (rangeEnd - rangeStart) / 2)
    if (midPoint < 0) midPoint = 0

    return midPoint >= 0 && data[midPoint] ? data[midPoint]?.time as number : data[data.length - 1]?.time as number

}

export const getChartCandlesNeeded = (range: LogicalRange, barsInfo: BarsInfo<any>) => {
    return Math.ceil(Math.ceil(range.to) + (barsInfo.barsAfter < 0 ? Math.floor(barsInfo.barsAfter) : Math.ceil(barsInfo.barsAfter)) - barsInfo.barsBefore)
}

export const prepareSeriesData = ({ candles, priceMultiplier, timezone, lastTickTimestamp, calculateCustomSeries, fillEmptySlots = true, timeBucket, calendarData }: { candles: Candle[], priceMultiplier: number, timezone: TimeZone, lastTickTimestamp?: number, calculateCustomSeries?: (candle: ChartCandle, previousCandle: ChartCandle) => number, fillEmptySlots?: boolean, timeBucket: SuperChartConfiguration_TimeBucket, calendarData: TradingCalendarData | undefined }): ChartCandle[] => {
    const resultCandles: ChartCandle[] = [];
    const tradingCalendarData: CalendarData = constructCalendarDataLookup(calendarData)
    candles.forEach((candle) => {
        const previousCandle = resultCandles[resultCandles.length - 1]
        const currentTime = convertToTimeZone(candle.startTimestamp / 1000, timezone) as Time
        if (previousCandle && previousCandle.time >= currentTime) return
        const currentCandle = {
            time: currentTime,
            open: candle.open / priceMultiplier,
            high: candle.high / priceMultiplier,
            low: candle.low / priceMultiplier,
            close: candle.close / priceMultiplier,
            volume: candle.volume,
            vwap: candle.vwap / priceMultiplier,
            impliedVolatility: candle.impliedVolatility * 100,
            topBid: candle.topBid,
            topAsk: candle.topAsk,
            referencedSecurityCode: candle.referencedSecurityCode,
            openInterest: candle.openInterest,
            callOpenInterest: candle.callOpenInterest,
            putOpenInterest: candle.putOpenInterest,
            preOpenVolume: candle.preOpenVolume,
            maxPain: candle.maxPain,
            delta: candle.optionGreeks?.delta ?? 0,
            gamma: candle.optionGreeks?.gamma ?? 0,
            rho: candle.optionGreeks?.rho ?? 0,
            vega: candle.optionGreeks?.vega ?? 0,
            theta: candle.optionGreeks?.theta ?? 0,
            lastTickTimestamp: lastTickTimestamp ? convertToTimeZone(lastTickTimestamp / 1000, timezone) : 0,
            deliveryPercentage: candle.deliveryPercentage
        } as ChartCandle
        if (fillEmptySlots && previousCandle) {
            const intermediateCandles = fillCandles({
                startCandle: previousCandle,
                endCandle: currentCandle,
                timeBucket,
                calendarData: tradingCalendarData,
                timezone
            })
            resultCandles.push(...intermediateCandles)
        }
        resultCandles.push(currentCandle)
        if (calculateCustomSeries) {
            const priorCandle = resultCandles.length >= 2 ? resultCandles[resultCandles.length - 2] : undefined
            resultCandles[resultCandles.length - 1].series = calculateCustomSeries(
                resultCandles[resultCandles.length - 1],
                priorCandle
            )
        }
    })
    return resultCandles
}

export const mergeCandles = (lastCandle: ChartCandle, updatedCandle: ChartCandle) => {
    return {
        ...lastCandle,
        ...updatedCandle,
        open: lastCandle.open,
        time: lastCandle.time,
        close: updatedCandle.close,
        volume: lastCandle.volume + updatedCandle.volume,
        low: Math.min(updatedCandle.low, lastCandle.low),
        high: Math.max(updatedCandle.high, lastCandle.high),
        lastTickTimestamp: updatedCandle.time as UTCTimestamp
    }
}

export const getInitialCandle = (): ChartCandle => ({
    open: 0,
    high: 0,
    low: 0,
    close: 0,
    time: 0 as Time,
    referencedSecurityCode: '',
    lastTickTimestamp: 0,
    volume: 0,
    vwap: 0,
    topAsk: 0,
    topBid: 0,
    impliedVolatility: 0,
    callOpenInterest: 0,
    delta: 0,
    maxPain: 0,
    openInterest: 0,
    preOpenVolume: 0,
    putOpenInterest: 0,
    gamma: 0,
    rho: 0,
    theta: 0,
    vega: 0,
    deliveryPercentage: 0
})


// TODO: Chart optimization: remove
export const convertSecurityCandles = (candles: Candle[], priceMultiplier: number): any[] => {
    return candles.map((candle) => {
        return {
            time: candle.startTimestamp / 1000,
            open: candle.open / priceMultiplier,
            high: candle.high / priceMultiplier,
            low: candle.low / priceMultiplier,
            close: candle.close / priceMultiplier,
            customValues: {
                volume: candle.volume,
                vwap: candle.vwap / priceMultiplier,
                impliedVolatility: candle.impliedVolatility,
                topBid: candle.topBid,
                topAsk: candle.topAsk,
                referencedSecurityCode: candle.referencedSecurityCode,
                lastTickTimestamp: (candle as any)?.lastTickTimestamp ? ((candle as any).lastTickTimestamp) / 1000 : 0,
                openInterest: candle.openInterest,
                callOpenInterest: candle.callOpenInterest,
                putOpenInterest: candle.putOpenInterest
            } as Record<string, unknown>,
            referencedSecurityCode: candle.referencedSecurityCode,
            lastTickTimestamp: (candle as any)?.lastTickTimestamp ? ((candle as any).lastTickTimestamp) / 1000 : 0
        } as any
    })
}

export const getChartTypeFromOutput = (outputType: IndicatorOutput_OutputType): ChartType => {
    switch (outputType) {
        case IndicatorOutput_OutputType.DRAWING:
            return ChartType.CHART_TYPE_UNKNOWN
        case IndicatorOutput_OutputType.LINE:
            return ChartType.LINE
        case IndicatorOutput_OutputType.COLUMN:
            return ChartType.COLUMN
        case IndicatorOutput_OutputType.HISTOGRAM:
            return ChartType.HISTOGRAM
        case IndicatorOutput_OutputType.AREA:
            return ChartType.AREA
        case IndicatorOutput_OutputType.FILL:
            return ChartType.FILL
        default:
            return ChartType.LINE
    }
}

/** Reserved color titles used to persist area-output fill colors inside `colors`. */
export const AREA_FILL_TOP_COLOR_TITLE = '__areaFillTop'
export const AREA_FILL_BOTTOM_COLOR_TITLE = '__areaFillBottom'

export const isAreaFillColorTitle = (title?: string): boolean =>
    title === AREA_FILL_TOP_COLOR_TITLE || title === AREA_FILL_BOTTOM_COLOR_TITLE

/** Picks the configurable line color for an output (skips reserved area fill entries). */
export const getOutputLineColor = (colors?: IndicatorColor[]): string | undefined =>
    colors?.find((c) => !isAreaFillColorTitle(c.title))?.colorCode

/**
 * Builds area series fill/line options from an output's `colors`. Fill colors fall
 * back to translucent variants of the line color when not explicitly configured.
 */
export const getAreaSeriesColorOptions = (
    colors?: IndicatorColor[]
): { lineColor?: string; topColor?: string; bottomColor?: string } => {
    const lineColor = getOutputLineColor(colors)
    const top = colors?.find((c) => c.title === AREA_FILL_TOP_COLOR_TITLE)?.colorCode
    const bottom = colors?.find((c) => c.title === AREA_FILL_BOTTOM_COLOR_TITLE)?.colorCode
    const options: { lineColor?: string; topColor?: string; bottomColor?: string } = {}
    if (lineColor) options.lineColor = lineColor
    if (top) {
        options.topColor = top
    } else if (lineColor) {
        options.topColor = tinycolor(lineColor).setAlpha(0.4).toRgbString()
    }
    if (bottom) {
        options.bottomColor = bottom
    } else if (lineColor) {
        options.bottomColor = tinycolor(lineColor).setAlpha(0).toRgbString()
    }
    return options
}

export const getLineStyleFromOutput = (outputStyle: IndicatorOutput_OutputStyle): LineStyle => {
    switch (outputStyle) {
        case IndicatorOutput_OutputStyle.DASHED:
            return LineStyle.Dashed
        case IndicatorOutput_OutputStyle.SOLID:
            return LineStyle.Solid
        case IndicatorOutput_OutputStyle.SPARSE_DOTTED:
            return LineStyle.SparseDotted
        case IndicatorOutput_OutputStyle.DOTTED:
            return LineStyle.Dotted
        case IndicatorOutput_OutputStyle.LARGE_DASHED:
            return LineStyle.LargeDashed
        default:
            return LineStyle.Solid
    }
}

export const getLineTypeFromChartSubType = (chartSubType: ChartSubType): LineType => {
    switch (chartSubType) {
        case ChartSubType.SIMPLE:
            return LineType.Simple
        case ChartSubType.CURVED_LINE:
            return LineType.Curved
        case ChartSubType.STEP_LINE:
            return LineType.WithSteps
        case ChartSubType.CROSS_LINE:
            return LineType.Cross
        case ChartSubType.CIRCLE_LINE:
            return LineType.Circle
        default:
            return LineType.Simple
    }
}

export type GenerateIndicatorDataCallbacks = {
    plotCallback: IndicatorPlotCallback,
    drawCallback: IndicatorDrawCallback,
    lineCallback: IndicatorLineCallback,
    fillCallback: IndicatorFillCallback,
    onDataLoad: IndicatorDataLoadCallback,
    /** Filled when the script calls plot/fill/hline/line/draw — mirrors runtime draw visibility. */
    plotActivityTracker?: Set<string>,
}

/** Whether the script is actively drawing this output (not display:NONE and has data). */
export const isIndicatorPlotActive = (options: PlotOptions): boolean => {
    if (options.display === 'NONE') return false
    if (options.isHline) return true
    return (options.series?.length ?? 0) > 0
}

export const recordIndicatorPlotActivity = (
    tracker: Set<string> | undefined,
    title: string,
    active: boolean
): void => {
    if (!tracker || !title) return
    if (active) {
        tracker.add(title)
    } else {
        tracker.delete(title)
    }
}

export function extractContractSwitchesFromCandles(candles: any[], referenceSecurities: any): ContractSwitch[] {
    if (!candles) {
        return []
    }
    const switches: ContractSwitch[] = []
    if (candles.length <= 1) {
        return switches
    }
    let prevDt = candles[0]
    for (let i = 1; i < candles.length; i++) {
        const currDt = candles[i]
        if (currDt.referencedSecurityCode !== prevDt.referencedSecurityCode) {
            switches.push({
                time: (prevDt.time as number) * 1000,
                fromContract: referenceSecurities[prevDt.referencedSecurityCode].symbol ?? prevDt.referencedSecurityCode as string,
                toContract: referenceSecurities[currDt.referencedSecurityCode].symbol ?? currDt.referencedSecurityCode as string
            })
        }
        prevDt = currDt
    }
    return switches
}

export const createIndicatorData = async (
    params: CreateIndicatorDataParams,
    callbacks: GenerateIndicatorDataCallbacks
): Promise<void> => {
    const {
        open, high, low, close, volume, vwap, impliedVolatility, openInterest,
        oiChange, callOpenInterest, putOpenInterest, timestamps, input, ta, series, indicator,
        plot, line, fill, draw, hline, deliveryPercentage, hlc3, hl2, ohlc4, calendarData,
    } = getIndicatorEvaluationContext(params, callbacks)
    const chartOffset = 0;

    const context = {
        open, high, low, close, volume, vwap, impliedVolatility, openInterest,
        oiChange, callOpenInterest, putOpenInterest, timestamps, input, ta, series, indicator,
        plot, line, fill, draw, hline, deliveryPercentage, hlc3, hl2, ohlc4, calendarData, chartOffset
    }
    const keys = Object.keys(context)
    const values = Object.values(context)
    const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor as new (...args: string[]) => (...args: unknown[]) => Promise<unknown>
    const fn = new AsyncFunction(...keys, params.script)
    const boundFn = fn.bind(createIndicatorData, ...values)
    await boundFn()
}
export const getIndicatorEvaluationContext = (params: CreateIndicatorDataParams, callbacks: GenerateIndicatorDataCallbacks) => {
    const { candles, inputValues, calendarData } = params
    const open: number[] = []
    const high: number[] = []
    const low: number[] = []
    const close: number[] = []
    const hl2: number[] = []
    const hlc3: number[] = []
    const ohlc4: number[] = []
    const tbt: number[] = []
    const volume: number[] = []
    const vwap: number[] = []
    const impliedVolatility: number[] = []
    const openInterest: number[] = []
    const callOpenInterest: number[] = []
    const putOpenInterest: number[] = []
    const timestamps: number[] = []
    const oiChange: number[] = []
    const deliveryPercentage: number[] = []
    let lastValidDeliveryPercentage = NaN
    let prevOi: number = 0
    candles.forEach(c => {
        open.push(c.open)
        high.push(c.high)
        low.push(c.low)
        close.push(c.close)
        hl2.push((c.high + c.low) / 2)
        hlc3.push((c.high + c.low + c.close) / 3)
        ohlc4.push((c.open+c.high+c.low+c.close)/4)
        if (c.deliveryPercentage !== 0) {
            lastValidDeliveryPercentage = c.deliveryPercentage
        }
        deliveryPercentage.push(lastValidDeliveryPercentage)

        volume.push(c.volume)
        vwap.push(c.vwap)
        impliedVolatility.push(c.impliedVolatility)
        timestamps.push((c.time as number) * 1000)
        openInterest.push(c.openInterest)

        if (!isNaN(c.openInterest)) {
            if (prevOi) {
                oiChange.push(c.openInterest - prevOi)
            } else {
                oiChange.push(Number.NaN)
            }
            prevOi = c.openInterest
        } else {
            oiChange.push(0)
        }

        callOpenInterest.push(c.callOpenInterest)
        putOpenInterest.push(c.putOpenInterest)
    })

    let input = {
        int: (data: { title: string, defval: number, minval?: number, maxval?: number, code: string }) => {
            const value = resolveIndicatorInputValue(inputValues, data)
            return parseInt(value ?? '') || data.defval
        },
        string: (data: { title: string, defval: string, code?: string }) => {
            return resolveIndicatorInputValue(inputValues, data) ?? data.defval
        },
        float: (data: {
            title: string,
            defval: number,
            minval?: number,
            maxval?: number,
            code?: string
        }) => parseFloat(resolveIndicatorInputValue(inputValues, data) ?? '') || data.defval,
        bool: (data: { title: string, defval: boolean, code?: string }) => {
            const value = resolveIndicatorInputValue(inputValues, data)
            if (value === 'false') return false
            if (value === 'true') return true
            return value ? !!value : data.defval
        },
        source: (data: { title: string; defval: string; options: string[]; code?: string }) => {
            return resolveIndicatorInputValue(inputValues, data) ?? data.defval
        },
        series: (data: InputSeriesParams) => {
            const value = resolveIndicatorInputValue(inputValues, data) ?? data.defval
            const indicatorRef = decodeIndicatorOutputSource(value)
            if (indicatorRef) {
                const indicatorSeries = params.resolveIndicatorOutputSeries?.(indicatorRef, candles.length)
                if (indicatorSeries?.length) {
                    return indicatorSeries
                }
                throw new Error('Selected indicator source is unavailable')
            }
            switch (value) {
                case 'close':
                    return close
                case 'open':
                    return open
                case 'high':
                    return high
                case 'low':
                    return low
                case 'volume':
                    return volume
                case 'hl2':
                    return hl2
                case 'hlc3':
                    return hlc3
                case 'ohlc4':
                    return ohlc4
                case 'tbt':
                    return vwap
                default:
                    return close
            }
        }
    }
    const error = (message: string) => {
        throw new Error(message)
    }

    return {
        input,
        open,
        high,
        low,
        close,
        volume,
        vwap,
        hl2,
        hlc3,
        ohlc4,
        calendarData,
        tbt,
        impliedVolatility,
        openInterest,
        oiChange,
        callOpenInterest,
        putOpenInterest,
        timestamps,
        deliveryPercentage,
        error,
        ta: getTaUtils(params),
        series: getSeriesFetcher(params, callbacks, timestamps),
        indicator: (data: { title: string, shorttitle: string, overlay: boolean }) => {
        },
        ...getPlotFunctions(params, callbacks, timestamps)
    }
}

export const getTaUtils = (params: CreateIndicatorDataParams) => {
    const { security, candles } = params
    return new IndicatorUtils(security, async (requiredCandles: number, referenceTime: number, timeBucket?: TimeBucket) => {

        const candleResponse = await getCandles({
            body: {
                security,
                timeBucket,
                referenceTime,
                forwardCandleCount: 0,
                backwardCandleCount: requiredCandles,
                dontGiveExtraCandles: true,
                dontApplyCorporateActionAdjustments: false
            }
        })
        return convertSecurityCandles(candleResponse.backwardCandles, security.priceMultiplier ?? 100)
    }, candles)
}

export const getSeriesFetcher = (params: CreateIndicatorDataParams, callbacks: GenerateIndicatorDataCallbacks, timestamps: number[]) => {
    const { security, timeBucket, data: existingData, stopBackwardFetch, stopForwardFetch } = params
    const { onDataLoad } = callbacks
    return {
        fetch: async ({ series, dontTransform = false, params }: {
            series: string,
            dontTransform: boolean,
            params: any
        }): Promise<any> => {
            const dataStartTime = (new Date(timestamps[0])).getTime(),
                dataEndTime = (new Date(timestamps[timestamps.length - 1])).getTime()
            let requestsTimestamps: Array<[number, number]> = []
            let resultData: GetSeriesResponse = { series: [] }
            let stopBackDataFetch: boolean | undefined = undefined,
                stopForwardDataFetch: boolean | undefined = undefined
            if (existingData && existingData.length) {
                let minTimestamp = Number.MAX_VALUE, maxTimestamp = Number.MIN_VALUE
                existingData.forEach(({ firstDatapointTime, lastDatapointTime }) => {
                    minTimestamp = Math.min(minTimestamp, (new Date(firstDatapointTime)).getTime())
                    maxTimestamp = Math.max(maxTimestamp, (new Date(lastDatapointTime)).getTime())
                })

                if (minTimestamp > dataStartTime && !stopBackwardFetch) {
                    requestsTimestamps.push([dataStartTime, minTimestamp - 10])
                } else {
                    requestsTimestamps.push([0, 0])
                }

                if (maxTimestamp < dataEndTime && !stopForwardFetch) {
                    requestsTimestamps.push([maxTimestamp + 10, dataEndTime])
                } else {
                    requestsTimestamps.push([0, 0])
                }
            } else {
                requestsTimestamps.push([dataStartTime, dataEndTime])
            }
            let newData: any = {}

            if (requestsTimestamps.length) {
                const seriesResponses = await Promise.all(requestsTimestamps.map(([start, end]) => (start && end) ? getSeriesData({
                    seriesName: series,
                    security: { exchange: security.exchange, code: security.code },
                    timeBucket: timeBucket,
                    startTime: start,
                    endTime: end,
                    parameters: convertMapToKeyValueArray(params ?? {})
                }) : null
                ))

                const backwardResponse = seriesResponses[0]
                const forwardResponse = seriesResponses[1]

                if (!existingData || !existingData.length) {
                    resultData = backwardResponse || { series: [] }
                } else {
                    resultData = { series: existingData }
                    const existingSeriesMap: Record<string, GetSeriesResponse_Series> = existingData.reduce((acc: Record<string, GetSeriesResponse_Series>, series) => {
                        acc[series.seriesName] = series
                        return acc
                    }, {} as Record<string, GetSeriesResponse_Series>)

                    if (backwardResponse) {
                        const totalSeries = backwardResponse.series.length
                        let zeroDataSeries = 0
                        backwardResponse.series.forEach((sr) => {
                            if (sr.dataPoints.length) {
                                const existingSeries = { ...existingSeriesMap[sr.seriesName] }
                                const newDataPoints = [...sr.dataPoints, ...(existingSeriesMap[sr.seriesName]?.dataPoints || [])]
                                existingSeries.dataPoints = newDataPoints
                                existingSeries.firstDatapointTime = new Date(newDataPoints[0].timestamp).toISOString()
                                existingSeries.lastDatapointTime = new Date(newDataPoints[newDataPoints.length - 1].timestamp).toISOString()
                                existingSeries.seriesName = sr.seriesName
                                existingSeriesMap[sr.seriesName] = existingSeries
                            } else {
                                zeroDataSeries += 1
                            }
                        })
                        if (totalSeries === zeroDataSeries) {
                            stopBackDataFetch = true
                        } else {
                            stopBackDataFetch = false
                        }
                        resultData = {
                            series: Object.values(existingSeriesMap)
                        }
                    }

                    if (forwardResponse) {
                        const totalSeries = forwardResponse.series.length
                        let zeroDataSeries = 0
                        forwardResponse.series.forEach((sr) => {
                            if (sr.dataPoints.length) {
                                const existingSeries = { ...existingSeriesMap[sr.seriesName] }
                                const newDataPoints = [...(existingSeriesMap[sr.seriesName]?.dataPoints || []), ...sr.dataPoints]
                                existingSeries.dataPoints = newDataPoints
                                existingSeries.firstDatapointTime = new Date(newDataPoints[0].timestamp).toISOString()
                                existingSeries.lastDatapointTime = new Date(newDataPoints[newDataPoints.length - 1].timestamp).toISOString()
                                existingSeries.seriesName = sr.seriesName
                                existingSeriesMap[sr.seriesName] = existingSeries
                            } else {
                                zeroDataSeries += 1
                            }
                        })
                        if (totalSeries === zeroDataSeries) {
                            stopForwardDataFetch = true
                        } else {
                            stopForwardDataFetch = false
                        }
                        resultData = {
                            series: Object.values(existingSeriesMap)
                        }
                    }
                }
            }

            if (resultData.series.length === 0) {
                return dontTransform ? {} : [{}]
            }
            if (dontTransform) {
                newData = resultData
            } else {
                var transformedResponse = transformSeriesResponse(timestamps, timeBucket, resultData)
                const res: any[] = []
                transformedResponse.forEach((tr, ind) => {
                    const obj: Record<string, any[]> = {}
                    for (let c = 0; c < resultData.series[ind].keys.length; c++) {
                        obj[resultData.series[ind].keys[c]] = tr[0].get(resultData.series[ind].keys[c]) ?? []
                    }
                    obj['labels'] = tr[1]
                    res.push(obj)
                })
                newData = res
            }

            onDataLoad(resultData.series, stopBackDataFetch, stopForwardDataFetch)
            return newData
        }
    }
}

export const getPlotFunctions = (params: CreateIndicatorDataParams, callbacks: GenerateIndicatorDataCallbacks, timestamps: number[]) => {
    const { outputValues, candleType } = params
    const { plotCallback, fillCallback, lineCallback, drawCallback, plotActivityTracker } = callbacks
    const plot = (options: PlotOptions): number[] => {
        recordIndicatorPlotActivity(plotActivityTracker, options.title, isIndicatorPlotActive(options))
        const customOutputOptions = Object.values(outputValues).find((ov) => ov.title === options.title)
        const updatedColor = replaceColorHashcodes(customOutputOptions?.colors?.map((color) => color.colorCode) ?? [], options.color ?? '')
        return plotCallback({
            ...options,
            series: options.series || [],
            color: updatedColor,
            withBreaks: candleType !== CandleTypes.OTHER
        })
    }

    return {
        plot,
        fill: (options: FillOptions) => {
            const fillActive =
                (options.seriesA?.length ?? 0) > 0 && (options.seriesB?.length ?? 0) > 0
            recordIndicatorPlotActivity(plotActivityTracker, options.title, fillActive)

            const customOutputOptions = Object.values(outputValues).find((ov) => ov.title === options.title)
            const updatedColor = replaceColorHashcodes(customOutputOptions?.colors?.map((color) => color.colorCode) ?? [], options.color ?? '')

            fillCallback({
                ...options,
                color: updatedColor
            })
        },
        hline: (options: HLineOptions) => {
            const { price, title, color, display, lineStyle } = options
            const hseries: number[] = Array(timestamps.length).fill(price)

            return plot({
                series: hseries,
                title: title,
                color: color,
                type: 'LINE',
                display,
                isHline: true,
                lineStyle
            })
        },
        line: (options: LineOptions) => {
            const lineActive = (options.dataPoints?.length ?? 0) > 0
            recordIndicatorPlotActivity(plotActivityTracker, options.title, lineActive)
            lineCallback({
                ...options
            })
        },
        draw: (options: DrawOptions) => {
            recordIndicatorPlotActivity(plotActivityTracker, options.title, true)
            drawCallback({ ...options })
        }
    }
}

export function compareTimeBuckets(a: TimeBucket, b: TimeBucket) {
    if (a?.timeSeries !== b?.timeSeries) {
        return a?.timeSeries - b?.timeSeries
    }

    return a?.value - b?.value
}

export function areTimeBucketsSame(a: TimeBucket, b: TimeBucket) {
    return (a?.timeSeries === b?.timeSeries) && (a?.value === b?.value)
}

export function getSeriesDataType(chartType: ChartType) {
    switch (chartType) {
        case ChartType.AREA:
            return
    }
}

export function transformSeriesResponse(timestamps: number[], timeBucket: TimeBucket, seriesResponse: GetSeriesResponse): [Map<string, number[]>, string[]][] {

    // const sr = seriesResponse.series[0]
    const response: any[] = []

    seriesResponse.series.forEach((sr) => {
        const mp = new Map<string, number[]>()
        const labels: string[] = []
        const timeWiseDataPoints = sr.dataPoints.reduce((acc: Map<number, GetSeriesResponse_DataPoint>, curr: GetSeriesResponse_DataPoint) => {
            const date = new Date(curr.timestamp)
            acc.set(curr.timestamp, curr)
            return acc
        }, new Map<number, GetSeriesResponse_DataPoint>())
        const dayWiseDataPoints = sr.dataPoints.reduce((acc: Map<number, GetSeriesResponse_DataPoint>, curr: GetSeriesResponse_DataPoint) => {
            const date = new Date(curr.timestamp)
            date.setHours(0, 0, 0, 0)
            acc.set(date.getTime(), curr)
            return acc
        }, new Map<number, GetSeriesResponse_DataPoint>())

        const timebucketComparision = compareTimeBuckets(timeBucket, {
            timeSeries: TimeBucket_TimeSeries.DAY,
            value: 1
        }) < 0
        timestamps.forEach((timestamp) => {
            const dt = new Date(timestamp)
            dt.setHours(0, 0, 0, 0)
            const dp = timeWiseDataPoints.get(timestamp)
            const dataPoint = dayWiseDataPoints.get(dt.getTime())
            labels.push(dataPoint?.label ?? '')
            sr.keys.forEach((key, ind) => {
                const val = (timebucketComparision ? dp?.values?.[ind] : dataPoint?.values?.[ind]) ?? NaN
                if (mp.has(key)) {
                    mp.set(key, [...mp.get(key)!, val])
                } else {
                    mp.set(key, [val])
                }
            })
        })
        response.push([mp, labels])
    })
    return response
}

export function getIndicatorPriceType(format: Indicator_Format): PriceFormat['type'] {
    switch (format) {
        case Indicator_Format.PRICE:
            return 'price'
        case Indicator_Format.PERCENT:
            return 'percent'
        case Indicator_Format.VOLUME:
            return 'volume'
        default:
            return 'price'
    }
}

const formatIndicatorValueWithPrecision = (value: number, precision: number): string =>
    toLocaleStringIn(value, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    })

export function formatIndicatorOutputValue(
    value: number,
    priceFormat: PriceFormat['type'],
    precision?: number
): string {
    if (!Number.isFinite(value)) return '-'
    if (precision === undefined) {
        switch (priceFormat) {
            case 'price':
                return toLocaleStringIn(value)
            case 'volume':
                return formatLargeNumber(value)
            case 'percent':
                return `${value.toFixed(2)}%`
            default:
                return value.toFixed(2)
        }
    }
    switch (priceFormat) {
        case 'percent':
            return `${formatIndicatorValueWithPrecision(value, precision)}%`
        case 'volume':
            return formatLargeNumber(value, precision)
        case 'price':
            return formatIndicatorValueWithPrecision(value, precision)
        default:
            return formatIndicatorValueWithPrecision(value, precision)
    }
}

export function getIndicatorOutputPriceFormat(
    priceFormat: PriceFormat['type'],
    precision?: number
): DeepPartial<PriceFormat> {
    if (precision === undefined) {
        return { type: priceFormat }
    }
    return {
        type: 'custom',
        formatter: (price: number) => formatIndicatorOutputValue(price, priceFormat, precision),
        minMove: precision > 0 ? Math.pow(10, -precision) : 1,
    }
}

export function applyColorToDataPoint(data: any, chartType: ChartType, color: string | undefined) {
    if (!color) return
    switch (chartType) {
        case ChartType.AREA: {
            data.lineColor = color
            data.bottomColor = color
            data.topColor = color
            break
        }
        case ChartType.CANDLESTICK:
        case ChartType.BAR:
        case ChartType.COLUMN:
        case ChartType.HISTOGRAM:
        case ChartType.LINE: {
            data.color = color
            break
        }
    }
}

export function extractColorFromDataPoint(data: any, chartType: ChartType): string | undefined {
    let color: string | undefined
    switch (chartType) {
        case ChartType.AREA: {
            color = data.topColor
            break
        }
        case ChartType.CANDLESTICK:
        case ChartType.BAR:
        case ChartType.HISTOGRAM:
        case ChartType.COLUMN:
        case ChartType.LINE: {
            color = data?.color
            break
        }
    }
    if (color === 'transparent') {
        color = data.customValues?.['backup_color'] as string
    }
    return color
}

// (lastCandle?.color ? (lastCandle?.color === 'transparent' ? lastCandle.customValues?.['backup_color'] as string : lastCandle.color) : series.color) ?? undefined

export const getPriceScaleId = (scale: Scale, defaultId: string): string => {
    switch (scale) {
        case 'LEFT':
            return 'left'
        case 'RIGHT':
            return 'right'
        default:
            return defaultId
    }
}

export function extractDateRange(range: SuperChartConfiguration_DateRange): [Date, Date] | undefined {
    return [new Date(range.startTime), new Date(range.endTime)]
}

export { getTimeIndex } from './getTimeIndex'

export const getIndicatorDrawingSettings = (type: keyof DrawingSettingsMap, chartOptions: ChartOptions, drawingSettings: any) => {
    switch (type) {
        case 'oiProfile': {
            const settings = drawingSettings?.settings ?? {}
            return {
                primaryCallColor: {
                    opacity: 1,
                    color: resolveOiProfileColor('primaryCallColor', settings['primaryCallColor']),
                },
                secondaryCallColor: {
                    opacity: 1,
                    color: resolveOiProfileColor('secondaryCallColor', settings['secondaryCallColor']),
                },
                secondaryPutColor: {
                    opacity: 1,
                    color: resolveOiProfileColor('secondaryPutColor', settings['secondaryPutColor']),
                },
                primaryPutColor: {
                    opacity: 1,
                    color: resolveOiProfileColor('primaryPutColor', settings['primaryPutColor']),
                },
                showLabels: false,
                showCallPutOnSameSide: drawingSettings?.settings?.['showCallPutOnSameSide'] ?? true,
                cumulative: drawingSettings?.settings?.['cumulative'] ?? true,
                indicator: drawingSettings?.settings?.['indicator'] ?? true,
                dataRenderType: drawingSettings?.settings?.['dataRenderType'] ?? 'openInterestChange',
                barThickness: clampOiProfileBarThickness(
                    Number(drawingSettings?.settings?.['barThickness'] ?? OI_PROFILE_DEFAULT_BAR_THICKNESS)
                ),
                barWidthPercent: clampOiProfileBarWidthPercent(
                    Number(drawingSettings?.settings?.['barWidthPercent'] ?? OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT)
                ),
                textColor: {
                    opacity: 1,
                    color: chartOptions.layout?.textColor ?? '#000000'
                }
            }
        }
        case 'oiHistorical': {
            return {
                callFillColor: {
                    opacity: 1,
                    color: drawingSettings?.settings?.['callFillColor'] ?? '#0E46A3'
                },
                callStrokeColor: {
                    opacity: 1,
                    color: drawingSettings?.settings?.['callStrokeColor'] ?? '#1E0342'
                },
                putFillColor: {
                    opacity: 1,
                    color: drawingSettings?.settings?.['putFillColor'] ?? '#C738BD'
                },
                putStrokeColor: {
                    opacity: 1,
                    color: drawingSettings?.settings?.['putFillColor'] ?? '#850F8D'
                },
                indicator: drawingSettings?.settings?.['indicator'] ?? true,
                dataRenderType: drawingSettings?.settings?.['dataRenderType'] ?? 'openInterestChange'
            }
        }
        case 'chartPatternOverlay': {
            const resolved = resolveChartPatternOverlaySettings(
                getPatternDrawingSettingsMap(drawingSettings),
            )
            const toColor = (hex: string, opacity = 1) => ({ color: hex, opacity })
            const background = resolved.backgroundColor || CP_DEFAULT_BACKGROUND_COLOR
            const bgColor = tinycolor(background)
            return {
                fillColor: toColor(bgColor.toHexString(), bgColor.getAlpha()),
                lineColor: toColor(resolved.priceLineColor || CP_DEFAULT_PRICE_LINE_COLOR),
                lineWidth: resolved.priceLineWidth,
                priceLineColor: toColor(resolved.priceLineColor || CP_DEFAULT_PRICE_LINE_COLOR),
                priceLineStyle: chartLineStyleToLineType(resolved.priceLineStyle),
                priceLineWidth: resolved.priceLineWidth,
                neckLineColor: toColor(resolved.neckLineColor || CP_DEFAULT_NECK_LINE_COLOR),
                neckLineStyle: chartLineStyleToLineType(resolved.neckLineStyle),
                neckLineWidth: resolved.neckLineWidth,
                showBackground: resolved.showBackground,
                backgroundColor: toColor(bgColor.toHexString(), bgColor.getAlpha()),
                showPaneLabels: resolved.showPaneLabels,
                paneLabelColor: toColor(resolved.paneLabelColor || CP_DEFAULT_PANE_LABEL_COLOR),
                paneLabelTextColor: toColor(resolved.paneLabelTextColor || CP_DEFAULT_PANE_LABEL_TEXT_COLOR),
                showLines: resolved.showLines,
                showPolylines: resolved.showPolylines,
                showTrendlines: resolved.showTrendlines,
                showPatternLines: resolved.showPatternLines,
                showPriceTarget: resolved.showPriceTarget,
                labelColorBullish: resolved.labelColorBullish,
                labelColorBearish: resolved.labelColorBearish,
                candleFillOpacity: resolved.candleFillOpacity,
                indicator: true,
            }
        }
    }
}

export function fitDrawingData(security: Security, type: keyof DrawingSettingsMap, data: any) {
    switch (type) {
        case 'oiProfile': {
            const oiData: Record<number, OiProfileDataType[]> = {}
            const expiryOIs = data as GetCurrentOpenInterestResponse_ExpiryOpenInterest[]
            expiryOIs.forEach((expiryOi) => {
                if (!expiryOi.expiry?.expiry) {
                    return
                }
                oiData[expiryOi.expiry?.expiry ?? NaN] = expiryOi.strikeOpenInterests.map((td: GetCurrentOpenInterestResponse_ExpiryOpenInterest_StrikeOpenInterest) => {
                    return {
                        callOpenInterest: td.callOi,
                        putOpenInterest: td.putOi,
                        callOpenInterestChange: td.callOi - td.previousCallOi,
                        putOpenInterestChange: td.putOi - td.previousPutOi,
                        strike: (td.strike / (security.priceMultiplier ?? 100)),
                        expiry: expiryOi.expiry
                    } as OiProfileDataType
                })
            })
            return oiData
        }
        case 'chartPatternOverlay': {
            const patterns = data?.patterns ?? []
            if (!patterns.length) return {}
            const allCandlestick = patterns.every(
                (pattern: { displayMode?: string }) => pattern.displayMode === 'candlestick',
            )
            return { patterns: allCandlestick ? patterns : patterns.slice(0, 1) }
        }
        default: {
            return data
        }
    }
}

export const getIndicatorDrawSettings = (
    type: keyof DrawingSettingsMap,
    chartOptions: ChartOptions,
    drawingSettings: any,
    data: any,
    security: Security
) => {
    const baseSettings = getIndicatorDrawingSettings(type, chartOptions, drawingSettings)
    const fitted = fitDrawingData(security, type, data)
    if (type === 'chartPatternOverlay') {
        return { ...baseSettings, ...fitted }
    }
    return { ...baseSettings, data: fitted }
}

export const getViewportCandles = (viewportCandleInfo: ViewportCandleInfo) => {
    let { rangeStart, rangeEnd, barsAfter } = viewportCandleInfo
    return (rangeEnd - rangeStart - (barsAfter < 0 ? barsAfter : 0)) || INITIAL_CANDLE_COUNT
}

export const buildComparableSecurityPanePlacements = (
    comparableSecurities: Security[],
    placementRecord?: Record<string, ComparableSecurityPanePlacement>,
    placementArray?: ComparableSecurityPanePlacement[]
): ComparableSecurityPanePlacement[] => {
    const n = comparableSecurities.length
    if (placementArray && placementArray.length === n) {
        return placementArray.map((p) => (p === 'new_pane' ? 'new_pane' : 'overlay'))
    }
    if (placementArray && placementArray.length > n) {
        return placementArray.slice(0, n).map((p) => (p === 'new_pane' ? 'new_pane' : 'overlay'))
    }
    if (placementArray && placementArray.length > 0 && placementArray.length < n) {
        return comparableSecurities.map((sec, i) => {
            if (i < placementArray!.length) {
                return placementArray![i] === 'new_pane' ? 'new_pane' : 'overlay'
            }
            const k = getSecurityKey(sec.exchange, sec.code)
            return placementRecord?.[k] === 'new_pane' ? 'new_pane' : 'overlay'
        })
    }
    return comparableSecurities.map((sec) => {
        const k = getSecurityKey(sec.exchange, sec.code)
        return placementRecord?.[k] === 'new_pane' ? 'new_pane' : 'overlay'
    })
}

export const legacyComparablePlacementRecord = (
    comparableSecurities: Security[],
    placements: ComparableSecurityPanePlacement[]
): Record<string, ComparableSecurityPanePlacement> => {
    const result: Record<string, ComparableSecurityPanePlacement> = {}
    comparableSecurities.forEach((sec, i) => {
        const k = getSecurityKey(sec.exchange, sec.code)
        result[k] = placements[i] === 'new_pane' ? 'new_pane' : 'overlay'
    })
    return result
}

export const normalizeComparableSecurityPanePlacement = (
    comparableSecurities: Security[],
    placement?: Record<string, ComparableSecurityPanePlacement>
): Record<string, ComparableSecurityPanePlacement> => {
    const arr = buildComparableSecurityPanePlacements(comparableSecurities, placement, undefined)
    return legacyComparablePlacementRecord(comparableSecurities, arr)
}

export const getDefaultChartConfig = (initialConfig?: Partial<ChartConfig>): ChartConfig => {
    return {
        timeBucket: {
            code: 'ONE_MINUTE',
            name: '1 minute',
            timeSeries: TimeBucket_TimeSeries.MINUTE,
            timeValue: 1
        },
        showOiProfile: false,
        chartType: ChartType.CANDLESTICK,
        comparableSecurities: [],
        comparableSecurityPanePlacement: {},
        comparableSecurityPanePlacements: [],
        leftScaleGroup: '',
        panes: [],
        panesConfig: {},
        seriesGroups: {},
        series: {},
        loading: false,
        scaleOptions: {
            rightScaleMode: PriceScaleMode.Normal,
            rightScaleVisible: true,
            leftScaleMode: PriceScaleMode.Normal,
            leftScaleVisible: false
        },
        scaleInvertOption: { invertScaleMode: false },
        replayMode: false,
        tradingSettings: defaultTradingSettings,
        alertSettings: defaultAlertSettings,
        canvasSettings: defaultCanvasSettings,
        ...(initialConfig || {})
    }
}

export const defaultTradingSettings = {
    showTradeFromChart: true,
    showOrderMarkersOnChart: true,
    showBuySellButtons: true,
    oneClickTrading: false,
}

export const defaultAlertSettings = {
    showAlertLines: true,
    alertLineColor: '#D1D4DC',
    alertLineColorOpacity: 0.92,
    onlyActiveAlerts: true,
}

export const getEffectiveAlertSettings = (
    chartConfig?: Pick<ChartConfig, 'alertSettings' | 'tradingSettings'>
) => {
    const alertSettings = chartConfig?.alertSettings ?? {}
    const legacyShowAlerts = (chartConfig?.tradingSettings as { showAlertsOnChart?: boolean } | undefined)
        ?.showAlertsOnChart
    return {
        ...defaultAlertSettings,
        ...alertSettings,
        showAlertLines:
            alertSettings.showAlertLines ??
            (legacyShowAlerts !== undefined ? legacyShowAlerts : defaultAlertSettings.showAlertLines),
    }
}

/** Opacity for the 0% / 100% series baseline in percentage and indexed-to-100 modes (lightweight-charts reads these from series options). */
const PERCENTAGE_BASE_LINE_OPACITY = 0.25

export const getPercentageModeBaseLineSeriesOptions = (
    mode: PriceScaleMode | undefined,
    theme: Theme
) => {
    if (mode !== PriceScaleMode.Percentage && mode !== PriceScaleMode.IndexedTo100) {
        return {}
    }
    return {
        baseLineVisible: true,
        baseLineColor: tinycolor(theme.palette.primary.main).setAlpha(PERCENTAGE_BASE_LINE_OPACITY).toRgbString(),
    }
}

export const isPercentageLikeScaleMode = (mode: PriceScaleMode | undefined): boolean =>
    mode === PriceScaleMode.Percentage || mode === PriceScaleMode.IndexedTo100

const readCandleSourceValue = (
    candle: ChartCandle | undefined,
    source: string
): number | undefined => {
    if (!candle) return undefined
    let value: number | undefined
    switch (source) {
        case 'open':
            value = candle.open
            break
        case 'high':
            value = candle.high
            break
        case 'low':
            value = candle.low
            break
        case 'hl2':
            value = (candle.high + candle.low) / 2
            break
        case 'hlc3':
            value = (candle.high + candle.low + candle.close) / 3
            break
        case 'ohlc4':
            value = (candle.open + candle.high + candle.low + candle.close) / 4
            break
        case 'close':
        default:
            value = candle.close ?? (candle as any).value
            break
    }
    return Number.isFinite(value) ? (value as number) : undefined
}

/**
 * Maps compare prices onto the primary's absolute level so that, with
 * percentageModeReferenceSourceIndex = 0 (primary base), each compare still
 * renders as starting at 0% / 100 and tracks its own % change.
 * X = primaryBase * (comparePrice / compareBase)
 */
export const remapComparableCandlesToPrimaryPercentageBase = (
    compareData: ChartCandle[],
    primaryData: ChartCandle[],
    rangeStart: number,
    compareSource: string = 'close',
    primarySource: string = 'close'
): ChartCandle[] => {
    if (!compareData.length || !primaryData.length) return compareData

    const from = Math.max(0, Math.floor(rangeStart))
    let primaryBase: number | undefined
    let compareBase: number | undefined

    for (let i = from; i < primaryData.length; i++) {
        const primaryCandle = primaryData[i]
        const pBase = readCandleSourceValue(primaryCandle, primarySource)
        if (pBase === undefined || pBase === 0) continue

        const compareIdx = getTimeIndex(compareData, primaryCandle.time as number)
        if (!Number.isFinite(compareIdx) || compareIdx < 0) continue
        if ((compareData[compareIdx]?.time as number) !== (primaryCandle.time as number)) continue
        const cBase = readCandleSourceValue(compareData[compareIdx], compareSource)
        if (cBase === undefined || cBase === 0) continue

        primaryBase = pBase
        compareBase = cBase
        break
    }

    if (primaryBase === undefined || compareBase === undefined || compareBase === 0) {
        return compareData
    }

    const factor = primaryBase / compareBase
    return compareData.map((candle) => ({
        ...candle,
        open: candle.open != null ? candle.open * factor : candle.open,
        high: candle.high != null ? candle.high * factor : candle.high,
        low: candle.low != null ? candle.low * factor : candle.low,
        close: candle.close != null ? candle.close * factor : candle.close,
    }))
}

/** Input code used to store the price source (open/high/low/close…) for a comparable-security series group. */
export const COMPARABLE_SECURITY_SOURCE_INPUT_CODE = 'source'

export const compareSecurityKey = (security: Security | undefined, key: string): boolean => {
    if (!security || !key) return false
    return getSecurityKey(security.exchange, security.code) === key
}

export const getEmptyTimeSlotsCount = (timeBucket: SuperChartConfiguration_TimeBucket, lastPointTimeInUTC: number, slotCount: number) => {

    let endDate = toDayStart(Date.now())

    let daysToAdd = 10
    switch (timeBucket.timeSeries) {
        case TimeBucket_TimeSeries.DAY:
            daysToAdd = 700
            break
        case TimeBucket_TimeSeries.HOUR:
            daysToAdd = 90
            endDate = new Date(endDate.setDate(endDate.getDate() + 90))
            break
    }
    endDate = new Date(endDate.setDate(endDate.getDate() + daysToAdd))
    const candleCount = calculateCandlesBetweenDates([new Date(lastPointTimeInUTC * 1000), endDate], {
        timeSeries: timeBucket.timeSeries,
        value: timeBucket.timeValue
    })

    return Math.max(candleCount, slotCount)
}


export const getEmptyTimeslots = (lastTimeInSecondsInITC: number, slotCount: number, timeBucket: SuperChartConfiguration_TimeBucket, calendarData: CalendarData): number[] => {
    if (Number.isNaN(lastTimeInSecondsInITC)) return [];
    const timestamps: number[] = []
    const timeWidth = getTimeWidthInSeconds(timeBucket)

    let lastCandleTimeInIST = lastTimeInSecondsInITC
    if (timeBucket.timeSeries === TimeBucket_TimeSeries.DAY) {
        lastCandleTimeInIST = toDayStart(lastCandleTimeInIST * 1000).getTime() / 1000;
        let count = slotCount;
        let maxIterations = slotCount * 10; // Safety mechanism to prevent infinite loops
        while (count > 0 && maxIterations > 0) {
            const nextTime = lastCandleTimeInIST + timeWidth
            const nextDate = new Date(nextTime * 1000)
            let dateString = formatDateToISODate(nextDate, '')
            if (!isTradingHoliday(dateString, calendarData)) {
                timestamps.push(nextTime)
                count--;
            }
            lastCandleTimeInIST = nextTime
            maxIterations--;
        }
    } else {
        let count = slotCount;
        let currentDayStartInIst = toDayStart(lastCandleTimeInIST * 1000).getTime() / 1000;
        let tradingDayGapThreshold = 10; // Increased threshold for better reliability
        
        while (count > 0 && tradingDayGapThreshold > 0) {
            const tradingSessions = getTradingSessionsForDate(currentDayStartInIst, calendarData);
            
            if (tradingSessions.length === 0) {
                // Use trading calendar to find next valid trading day
                const currentDate = new Date(currentDayStartInIst * 1000);
                currentDate.setDate(currentDate.getDate() + 1); // Move to next day
                
                // Convert calendarData to TradingCalendarData format for getNextValidTradingDay
                const tradingCalendarData: TradingCalendarData = {
                    exchange: 0, // Default exchange value
                    holidays: Array.from(calendarData.holidays).map(date => ({ 
                        exchange: 0, 
                        name: 'Holiday', 
                        eventType: 0, 
                        date 
                    })),
                    specialTradingDays: Array.from(calendarData.specialTradingDays).map(date => ({
                        date,
                        sessionTypes: calendarData.dayToSessions.get(date) || []
                    })),
                    tradingSessions: []
                };
                
                const nextTradingDay = getNextValidTradingDay(currentDate, tradingCalendarData);
                if (nextTradingDay) {
                    currentDayStartInIst = nextTradingDay.getTime() / 1000;
                } else {
                    // Fallback to simple day increment if getNextValidTradingDay fails
                    currentDayStartInIst += 24 * 60 * 60;
                }
                
                tradingDayGapThreshold--;
                continue;
            }
            
            tradingDayGapThreshold = 10; // Reset gap threshold for next day

            tradingSessions.forEach((session) => {
                if (count <= 0) return; // Exit early if we have enough slots
                
                const sessionStart = currentDayStartInIst + session.startTime;
                const sessionEnd = currentDayStartInIst + session.endTime;
                
                while (count > 0) {
                    const nextTime = lastCandleTimeInIST + timeWidth;
                    if (nextTime < sessionStart) {
                        timestamps.push(sessionStart);
                        lastCandleTimeInIST = sessionStart;
                        count--;
                    } else if (nextTime <= sessionEnd) {
                        timestamps.push(nextTime);
                        lastCandleTimeInIST = nextTime;
                        count--;
                    } else {
                        break; // Move to next session or next day
                    }
                }
            });
            
            // Move to next day
            currentDayStartInIst += 24 * 60 * 60;
        }
    }

    return timestamps;
}

export const getNextChartTimeStamps = (lastPointTimeInUTC: number, slotsCount: number, timeBucket: SuperChartConfiguration_TimeBucket, holidays: Set<string>): number[] => {
    const thresholdSeconds = getTimeWidthInSeconds(timeBucket);


    let lastTime = Number.isNaN(lastPointTimeInUTC) ? (new Date().setHours(9, 15, 0, 0) / 1000) - thresholdSeconds : lastPointTimeInUTC
    const timestamps: number[] = []

    const isInMarketTime = (timestamp: number): boolean => {
        const dt = new Date(timestamp * 1000)
        if (dt.getHours() == 9 && dt.getMinutes() < 15) return false
        if (dt.getHours() == 15 && dt.getMinutes() > 29) return false
        return (dt.getHours() >= 9 && dt.getHours() <= 15)
    }

    const getNextGoodDay = (year: number, month: number, date: number): Date => {
        const dt = new Date(year, month, date)
        dt.setDate(dt.getDate() + 1)
        while (isHoliday(dt)) {
            dt.setDate(dt.getDate() + 1)
        }
        if (isIntraday(timeBucket.timeSeries)) {
            dt.setHours(9, 15, 0, 0)
        }
        return dt
    }

    const isHoliday = (dt: Date) => holidays.has(formatDateToISODate(dt, '')) || [0, 6].includes(dt.getDay())

    let numberOfSlots = getEmptyTimeSlotsCount(timeBucket, lastTime, slotsCount)

    while (numberOfSlots--) {
        const nextTime = lastTime + thresholdSeconds
        const d = new Date(nextTime * 1000)
        if (!isHoliday(d) && isInMarketTime(nextTime)) {
            timestamps.push(nextTime)
            lastTime = nextTime
        } else {
            const nextGoodDay = getNextGoodDay(d.getFullYear(), d.getMonth(), d.getDate())
            timestamps.push(nextGoodDay.getTime() / 1000)
            lastTime = nextGoodDay.getTime() / 1000
        }
    }
    return timestamps
}

export const fillNanValues = (arr: number[]) => {
    const newArr = [...arr]
    let lastValue = 0
    for (let i = 0; i < newArr.length; i++) {
        if (isFinite(newArr[i])) {
            lastValue = newArr[i]
        } else {
            newArr[i] = lastValue
        }
    }
    return newArr
}

export const getNearestTimeBucketStartTimeStampToGivenTime = (tradingSessions: TradingSession[], timeStamp: number, timebucket: SuperChartConfiguration_TimeBucket) => {
    const currentTime = Date.now()
    const tradingSession = tradingSessions.find(t => {
        const marketOpenStartTime = new Date(t.startTime).getTime()
        const marketOpenEndTime = new Date(t.endTime).getTime()
        return currentTime >= marketOpenStartTime && currentTime <= marketOpenEndTime
    })
    if (!tradingSession) return undefined
    const timeWidth = getTimeWidthInSeconds(timebucket) * 1000
    const marketStart = tradingSession.startTime
    return marketStart + Math.floor((timeStamp - marketStart) / timeWidth) * timeWidth
}

export const getDefaultChartPanesConfig = async (
    chartType: ChartType,
    expressionMode: boolean,
    expression: string,
    securityList: string[],
    comparableSecurities: Security[],
    chartViewKey?: ChartViewKey,
    displayName = '',
    disableVolume = false,
    priceScaleId: Scale = 'RIGHT',
    resolvedPrimarySecurity?: Security
) => {
    let primaryPane: PaneConfig,
        seriesGroups: Record<string, SeriesGroup> = {},
        seriesObject: Record<string, Series> = {}
    const primarySecurity = expressionMode
        ? undefined
        : (resolvedPrimarySecurity ?? (securityList[0] ? await getSecurityAsync(securityList[0]) : undefined))
    const securityDisplayName =
        expressionMode ? displayName || convertExpressionToDisplayExpression(expression!) : primarySecurity?.displayName
    const primarySeriesGroup: SeriesGroup = {
        id: uuid(),
        category: SeriesGroupCategory.SERIES_SECURITY,
        name: securityDisplayName || '',
        primary: true,
        scale: priceScaleId,
        securityList,
        compareMode: comparableSecurities.length > 0,
        scriptCode: expressionMode ? expression! : primarySecurity?.code,
        series: [],
        addedTime: Date.now(),
        inputValues: {},
        outputValues: {}
    }

    const primarySeries: Series = {
        id: uuid(),
        chartType,
        chartSubType: ChartSubType.SIMPLE,
        title: displayName || '',
        code: displayName || '',
        name: displayName || '',
        priceFormat: 'price' as PriceFormat['type']
    }

    const blankSeries: Series = {
        id: BLANK_SERIES_ID,
        chartType: ChartType.CANDLESTICK,
        chartSubType: ChartSubType.SIMPLE,
        title: '',
        code: '',
        name: '',
        priceFormat: 'price' as PriceFormat['type']
    }

    primarySeriesGroup.series.push(primarySeries.id)
    primarySeriesGroup.series.push(blankSeries.id)

    seriesObject[primarySeries.id] = primarySeries
    seriesObject[blankSeries.id] = blankSeries
    seriesGroups[primarySeriesGroup.id] = primarySeriesGroup

    const volumeSeriesGroupId = uuid();
    if (!disableVolume) {

        const volumeSeriesGroup: SeriesGroup = {
            id: volumeSeriesGroupId,
            category: SeriesGroupCategory.SERIES_VOLUME,
            name: 'Volume',
            scale: 'RIGHT',
            primary: false,
            securityList,
            compareMode: comparableSecurities.length > 0,
            scriptCode: VOLUME_INDICATOR_CODE,
            series: [],
            addedTime: Date.now(),
            inputValues: {},
            outputValues: {}
        }
        const volumeSeries = {
            id: uuid(),
            chartType: ChartType.COLUMN,
            chartSubType: ChartSubType.SIMPLE,
            title: 'Volume',
            code: 'VOLUME',
            name: 'Volume',
            priceFormat: 'volume' as PriceFormat['type'],
            drawing: false,
            drawingType: '',
            drawingSettings: {
                settings: {}
            },
            color: candleUpColorAlpha
        }

        volumeSeriesGroup.series.push(volumeSeries.id)
        volumeSeriesGroup.outputValues[volumeSeries.id] = {
            title: 'Volume',
            type: IndicatorOutput_OutputType.COLUMN,
            code: 'VOLUME',
            colors: [
                {
                    title: 'Color 0',
                    colorCode: candleUpColorAlpha
                },
                {
                    title: 'Color 1',
                    colorCode: '#ff525280'
                }
            ],
            thickness: 1,
            drawingType: '',
            drawingSettings: {
                settings: {}
            }
        }
        seriesObject[volumeSeries.id] = volumeSeries
        seriesGroups[volumeSeriesGroup.id] = volumeSeriesGroup

    }
    primaryPane = {
        id: uuid(),
        primary: true,
        seriesGroups: [primarySeriesGroup.id]
    }


    if (!disableVolume && ![ChartViewKey.SPREAD, ChartViewKey.STRATEGY_CHART, ChartViewKey.STRATEGY].includes(chartViewKey)) {
        primaryPane.seriesGroups.push(volumeSeriesGroupId)
    };

    return {
        primaryPane,
        seriesGroups,
        seriesObject
    }
}

type FillCandleParams = { startCandle: ChartCandle, endCandle: ChartCandle, timeBucket: SuperChartConfiguration_TimeBucket, calendarData: CalendarData | undefined, timezone: TimeZone }

export const fillCandles = ({ startCandle, endCandle, timeBucket, calendarData, timezone }: FillCandleParams): ChartCandle[] => {

    if (!calendarData) return [];

    const candles: ChartCandle[] = [];
    const timeWidth = getTimeWidthInSeconds(timeBucket)
    let startTimeInSeconds = convertToUTC(startCandle.time as number, timezone)
    const endTimeInSeconds = convertToUTC(endCandle.time as number, timezone)
    if (startTimeInSeconds >= endTimeInSeconds) return []

    if (timeBucket.timeSeries === TimeBucket_TimeSeries.DAY) {
        const secondsInDay = 24 * 60 * 60;

        let currentDay = startTimeInSeconds + secondsInDay
        let currentDate = new Date(currentDay * 1000)
        let dateString = formatDateToISODate(currentDate, '')
        while (currentDay < endTimeInSeconds) {
            if (!isTradingHoliday(dateString, calendarData)) {
                const candle: ChartCandle = {
                    ...startCandle,
                    time: convertToTimeZone(currentDay, timezone) as Time,
                    open: startCandle.close,
                    high: startCandle.close,
                    low: startCandle.close,
                    volume: 0
                }
                startTimeInSeconds = currentDay
                currentDay += secondsInDay
                candles.push(candle)
            }

            return candles;
        }
    } else {
        let currentDate = toDayStart(startTimeInSeconds * 1000)

        while (true) {
            const dayStart = currentDate.getTime() / 1000
            const tradingSessions = getTradingSessionsForDate(dayStart, calendarData)
            if (!tradingSessions || tradingSessions.length === 0) return [];

            let lastCandleStartTime = 0;
            for (let c = 0; c < tradingSessions.length; c++) {
                const session = tradingSessions[c];
                const sessionStart = dayStart + session.startTime;
                const sessionEnd = dayStart + session.endTime;

                while (true) {
                    const candleTime = lastCandleStartTime ? lastCandleStartTime + timeWidth : Math.max(sessionStart, startTimeInSeconds + timeWidth);
                    if (candleTime >= endTimeInSeconds) {
                        lastCandleStartTime = 0;
                        return candles;
                    }
                    if (candleTime >= sessionEnd) {
                        lastCandleStartTime = 0;
                        break;
                    }
                    const candle: ChartCandle = {
                        ...startCandle,
                        time: convertToTimeZone(candleTime, timezone) as Time,
                        open: startCandle.close,
                        high: startCandle.close,
                        low: startCandle.close,
                        volume: 0
                    }
                    lastCandleStartTime = candleTime
                    candles.push(candle)
                }
            }
            currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000)
        }
    }
    return [];

}

export const getCurrentCandleStartTime = (timeInSecondsInIst: number, timeBucket: SuperChartConfiguration_TimeBucket, calendarData: CalendarData): number => {
    const dayStart = toDayStart(timeInSecondsInIst * 1000).getTime() / 1000;
    const sessions = getTradingSessionsForDate(timeInSecondsInIst, calendarData);
    const candleWidth = getTimeWidthInSeconds(timeBucket);

    if (isIntraday(timeBucket.timeSeries)) {
        for (let c = 0; c < sessions.length; c++) {
            const session = sessions[c]
            if (dayStart + session.startTime <= timeInSecondsInIst && timeInSecondsInIst < dayStart + session.endTime) {
                return Math.floor((timeInSecondsInIst - dayStart - session.startTime) / candleWidth) * candleWidth + dayStart + session.startTime
            }
        }
    }
    return dayStart
}

export const getActiveOrNextTradingSession = (timeInSecondsInIst: number, calendarData: CalendarData): TradingSession | undefined => {
    const date = new Date(timeInSecondsInIst * 1000);
    const dateString = formatDateToISODate(date, '');
    if (isTradingHoliday(dateString, calendarData)) return undefined;

    const tradingSessions = getTradingSessionsForDate(timeInSecondsInIst, calendarData);
    if (tradingSessions.length === 0) return undefined;

    const dayStart = toDayStart(timeInSecondsInIst * 1000).getTime() / 1000;
    return tradingSessions.find((session) => timeInSecondsInIst >= dayStart + session.startTime && timeInSecondsInIst < dayStart + session.endTime)
        || tradingSessions.find((session) => timeInSecondsInIst < dayStart + session.startTime);    
}

export const getTradingSessionsForDate = (timeInSecondsInIst: number, calendarData: CalendarData): TradingSession[] => {
    const date = new Date(timeInSecondsInIst * 1000);
    const dateString = formatDateToISODate(date, '');
    if (isTradingHoliday(dateString, calendarData)) return [];
    if (calendarData.specialTradingDays.has(dateString)) {
        return (calendarData.dayToSessions.get(dateString)?.map((type: string) => calendarData.typeToSessions.get(type)) ?? []).filter(v => v) as TradingSession[]
    } else {
        const morningSession = calendarData.typeToSessions.get("MORNING_SESSION")
        return morningSession ? [morningSession] : []
    }
}

/**
 * Elapsed regular-session millis between two UTC epoch timestamps, skipping overnight /
 * weekend / holiday gaps. Uses IST session boundaries (same as chart calendar helpers).
 * Negative when toMs < fromMs.
 */
export function getSessionElapsedMillis(fromMs: number, toMs: number, calendarData: CalendarData): number {
    if (fromMs === toMs) return 0
    const negative = toMs < fromMs
    const startIstSec = utcToIST(Math.min(fromMs, toMs) / 1000)
    const endIstSec = utcToIST(Math.max(fromMs, toMs) / 1000)

    let elapsedSec = 0
    let dayStart = toDayStart(startIstSec * 1000).getTime() / 1000
    const endDayStart = toDayStart(endIstSec * 1000).getTime() / 1000

    while (dayStart <= endDayStart) {
        const sessions = getTradingSessionsForDate(dayStart, calendarData)
        for (const session of sessions) {
            const sessionStart = dayStart + session.startTime
            const sessionEnd = dayStart + session.endTime
            const overlapStart = Math.max(startIstSec, sessionStart)
            const overlapEnd = Math.min(endIstSec, sessionEnd)
            if (overlapStart < overlapEnd) {
                elapsedSec += overlapEnd - overlapStart
            }
        }
        dayStart += 86400
    }

    const elapsedMs = elapsedSec * 1000
    return negative ? -elapsedMs : elapsedMs
}

export const getActiveTradeSession = (timeInSecondsInIst: number, tradingSessions: TradingSession[]): TradingSession | undefined => {
    const dayStart = toDayStart(timeInSecondsInIst * 1000).getTime() / 1000;
    return tradingSessions.find((session) => timeInSecondsInIst >= dayStart + session.startTime && timeInSecondsInIst < dayStart + session.endTime)
}

export const getNextIntradayCandleStartTime = (timeInSecondsInIst: number, timeBucket: SuperChartConfiguration_TimeBucket, calendarData: CalendarData): number => {
    const timeWidth = getTimeWidthInSeconds(timeBucket);
    const date = new Date(timeInSecondsInIst * 1000);
    const sessions = getTradingSessionsForDate(timeInSecondsInIst, calendarData);
    const currentCandleStartTime = getCurrentCandleStartTime(timeInSecondsInIst, timeBucket, calendarData);


    while (true) {
        const nextTime = timeInSecondsInIst + timeWidth
        const dt = new Date(nextTime * 1000)
        const dateString = formatDateToISODate(dt, '')
        if (calendarData.specialTradingDays.has(dateString) || calendarData.holidays.has(dateString) || isWeekend(dt)) {
            timeInSecondsInIst = nextTime
        } else {
            return nextTime
        }
    }
    return 0;
}

export const isTradingHoliday = (date: string, calendarData: CalendarData): boolean => {

    const dt = parseYYYYMMDDDate(date)
    if (!dt) return false
    return (!calendarData.specialTradingDays.has(date) && (calendarData.holidays.has(date) || isWeekend(dt!!)))
}

export const onOrNextTradingSessionsOfTheDay = (time: number, calendarDataLookup: CalendarData): TradingSession[] => {
    const date = new Date(time)
    const timeInSeconds = (3600 * date.getHours()) + (60 * date.getMinutes()) + date.getSeconds()
    const sessions = getTradingSessionsOfTheDay(date, calendarDataLookup)
    return sessions.filter((session) => {
        return session.startTime > timeInSeconds || (session.startTime <= timeInSeconds && session.endTime >= timeInSeconds)
    })
}

export const onOrBeforeTradingSessionsOfTheDay = (time: number, calendarDataLookup: CalendarData): TradingSession[] => {
    const date = new Date(time)
    const timeInSeconds = (3600 * date.getHours()) + (60 * date.getMinutes()) + date.getSeconds()
    const sessions = getTradingSessionsOfTheDay(date, calendarDataLookup)
    return sessions.filter((session) => {
        return session.endTime < timeInSeconds || (session.startTime <= timeInSeconds && session.endTime >= timeInSeconds)
    })
}

export const onOrBetweenTradingSessionsOfTheDay = (time1: number, time2: number, calendarDataLookup: CalendarData): TradingSession[] => {
    const date1 = new Date(time1)
    const date2 = new Date(time2)
    const timeInSeconds = getTimeInSeconds(date1)
    const time2InSeconds = getTimeInSeconds(date2)
    const sessions = getTradingSessionsOfTheDay(date1, calendarDataLookup)
    return sessions.filter((session) => {
        return !(session.endTime < timeInSeconds || session.startTime > time2InSeconds)
    })
}

export const getTradingSessionsOfTheDay = (date: Date, calendarDataLookup: CalendarData): TradingSession[] => {
    const dateString = formatDateToISODate(date, '')
    if (isTradingHoliday(dateString, calendarDataLookup)) {
        return []
    } else if (calendarDataLookup.specialTradingDays.has(dateString)) {
        return (calendarDataLookup.dayToSessions.get(dateString)?.map((type: string) => calendarDataLookup.typeToSessions.get(type)) ?? []).filter((s) => s !== undefined) as TradingSession[]
    } else {
        const morningSession = calendarDataLookup.typeToSessions.get("MORNING_SESSION")

        return morningSession ? [morningSession] : []
    }
}

const getEndTimeInSecondsFromTradingDaySession = (daySession: TradingDaySession) => {

    const date = new Date(daySession.date)
    date.setUTCHours(0, 0, 0, 0)

    return (date.getTime() + (daySession.endTime * 1000)) / 1000

}

const getStartTimeInSecondsFromTradingDaySession = (daySession: TradingDaySession) => {

    const date = new Date(daySession.date)
    date.setUTCHours(0, 0, 0, 0)

    return (date.getTime() + (daySession.startTime * 1000)) / 1000

}

